import type { Item, ItemPower, ItemPowerEffect } from '@dcc/rules/content/types.ts';
import { describeElapsed } from '@dcc/rules/rules/gameTime.ts';
import { describeItemPowerRules } from '@dcc/rules/rules/itemPower.ts';
import type { Modifier } from '@dcc/rules/rules/effects.ts';
import { STAT_LABELS } from '@dcc/rules/domain/stats.ts';
import { GEAR_SLOT_LABELS } from '@dcc/rules/rules/inventory.ts';

/** Just enough of the catalogue to name what an Item refers to. */
export interface ItemSummaryLookup {
  skillName(skillId: string): string | undefined;
  attack(attackId: string): { baseDamage: { dice: string; stat: string | null; type: string } } | undefined;
}

const KIND_LABELS: Record<Item['kind'], string> = {
  gear: 'Gear', potion: 'Potion', scroll: 'Scroll', container: 'Container', misc: 'Misc',
};

const signed = (amount: number) => (amount >= 0 ? `+${amount}` : `${amount}`);
const titled = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * One Modifier as a player reads it. Display only: nothing is calculated here,
 * and an unfamiliar shape is described rather than hidden.
 */
export function describeModifier(modifier: Modifier, lookup: ItemSummaryLookup): string {
  const target = modifier.target;
  const amount = typeof modifier.amount === 'number' ? modifier.amount : 0;
  switch (target.kind) {
    case 'stat': return `${STAT_LABELS[target.stat].full} ${signed(amount)}`;
    case 'skill-rank': return `${lookup.skillName(target.skillId) ?? target.skillId} Rank ${signed(amount)}`;
    case 'damage-resistance': return `Damage Resistance ${signed(amount)}`;
    case 'elemental-resistance': return `${titled(target.damageType)} Resistance`;
    case 'vulnerability': return `Vulnerable to ${titled(target.damageType)}`;
    case 'immunity': return `Immune to ${titled(target.damageType)}`;
    case 'evade': return `Evade ${signed(amount)}`;
    case 'move': return modifier.op === 'multiply' ? `Move ×${amount}` : `Move ${signed(amount)} ft`;
    case 'step': return modifier.op === 'multiply' ? `Step ×${amount}` : `Step ${signed(amount)} ft`;
    case 'max-mana': return `Max Mana ${signed(amount)}`;
    case 'advantage': return target.scope.kind === 'all' ? 'Advantage on all Checks' : 'Advantage on some Checks';
    case 'disadvantage': return target.scope.kind === 'all' ? 'Disadvantage on all Checks' : 'Disadvantage on some Checks';
    case 'check-modifier': return `Checks ${signed(amount)}`;
  }
}

export interface ItemSummary {
  /** "Gear · Head", "Potion · used up", "Weapon · Hands/Holding". */
  kindLine: string;
  /** What it does while equipped, one phrase each. Empty when it does nothing mechanical. */
  effects: string[];
}

/** Enough about an Item for a player to understand what it is and what it does. */
export function summarizeItem(item: Item, lookup: ItemSummaryLookup): ItemSummary {
  const parts: string[] = [item.weaponAttackId !== null ? 'Weapon' : KIND_LABELS[item.kind] ?? 'Item'];
  if (item.slot !== null) parts.push(item.slot === 'hands' && item.handUse === 'worn' ? 'Hands (worn)' : GEAR_SLOT_LABELS[item.slot]);
  if (item.consumable) parts.push('used up');

  const effects: string[] = [];
  if (item.weaponAttackId !== null) {
    const attack = lookup.attack(item.weaponAttackId);
    const skill = lookup.skillName(item.weaponAttackId) ?? item.weaponAttackId;
    effects.push(attack
      ? `${skill} attack: ${attack.baseDamage.dice}${attack.baseDamage.stat ? ` + ${attack.baseDamage.stat.toUpperCase()}` : ''} ${attack.baseDamage.type}`
      : `${skill} attack`);
  }
  if (item.use?.kind === 'restore-mana-full') effects.push('Restores all Mana when used');
  for (const modifier of item.modifiers) effects.push(describeModifier(modifier, lookup));
  if (item.power) effects.push(describePower(item.power, lookup));
  return { kindLine: parts.join(' · '), effects };
}

/** One thing a Power does, as a phrase: "heals 3 slots", "2d6 + INT fire damage". */
export function describePowerEffect(effect: ItemPowerEffect, lookup: ItemSummaryLookup): string {
  switch (effect.kind) {
    case 'heal-slots': return `heals ${effect.slots} Health Bar slot${effect.slots === 1 ? '' : 's'}`;
    case 'restore-mana': return effect.amount === 'full' ? 'restores all Mana' : `restores ${effect.amount} Mana`;
    case 'damage': return `${effect.dice}${effect.stat ? ` + ${effect.stat.toUpperCase()}` : ''} ${effect.damageType} damage`;
    case 'buff': {
      const duration = effect.duration.kind === 'rounds'
        ? `${effect.duration.rounds} round${effect.duration.rounds === 1 ? '' : 's'}`
        : effect.duration.kind === 'seconds' ? describeElapsed(effect.duration.seconds) : 'the fight';
      return `${effect.label} (${effect.modifiers.map((modifier) => describeModifier(modifier, lookup)).join(', ')}) for ${duration}`;
    }
  }
}

/** A whole Power in one line: what it does, then what it costs. */
export function describePower(power: ItemPower, lookup: ItemSummaryLookup): string {
  const does = power.effects.map((effect) => describePowerEffect(effect, lookup)).join('; ');
  const rules = describeItemPowerRules(power);
  return `${power.name}: ${does}${rules ? ` [${rules}]` : ''}`;
}
